import React, { useRef, useState } from 'react';
import { uploadAttachment, UploadPurpose } from '../api/gallery';
import { ApiError, imgSrc } from '../api/request';
import { confirmDialog } from '../utils/dialog';

// 图片上传组件: 点击/拖拽上传单张图 (前端直传 R2 + 后端注册元数据).
// 上传成功后通过 onChange 把永久 CDN 地址 (https://static-res.atomm.com/...) 传出去.
// 已有图就显示缩略图 + 替换/清除按钮.
//
// 布局约定: 方框只画图, 占位文案/上传中/错误一律排在方框外面 —— 早先文案挤在 96px 方框里
// 会溢出压到相邻控件上 (小贴士图标那一栏最明显), 别再把文字塞回框内.

// size 控制方框边长: sm=96px (列表里的缩略图位), md=128px, lg=160px (弹窗里的主要字段).
type Size = 'sm' | 'md' | 'lg';

const BOX_CLASS: Record<Size, string> = {
    sm: 'h-24 w-24',
    md: 'h-32 w-32',
    lg: 'h-40 w-40',
};

interface Props {
    value?: string;
    onChange: (url: string) => void;
    placeholder?: string;
    size?: Size;
    /** 上传用途: texture = 材料贴图 (≤50MB, jpg/png/svg/webp); 不传 = 通用图片. */
    purpose?: UploadPurpose;
    /** 点击缩略图的回调 (如打开大图预览); 不传则缩略图不可点. */
    onPreview?: (src: string) => void;
}

// 各用途的大小上限与可选格式, 与后端 attachment.go 的 uploadLimits 对齐, 超限提前拦掉, 不白传一趟.
// exts 非空时按扩展名再校验一次 (拖拽上传不受 accept 约束).
const LIMITS: Record<'default' | UploadPurpose, { maxMB: number; accept: string; exts?: string[] }> = {
    default: { maxMB: 20, accept: 'image/jpeg,image/png,image/webp,image/gif' },
    texture: { maxMB: 50, accept: '.jpg,.jpeg,.png,.svg,.webp', exts: ['.jpg', '.jpeg', '.png', '.svg', '.webp'] },
};

function ImageUploader({ value, onChange, placeholder, size = 'sm', purpose, onPreview }: Props) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const box = BOX_CLASS[size];
    const { maxMB, accept, exts } = LIMITS[purpose || 'default'];

    const handleFile = async (file: File) => {
        const dot = file.name.lastIndexOf('.');
        const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
        if (exts && !exts.includes(ext)) {
            setError(`只支持 ${exts.map((x) => x.slice(1)).join(' / ')} 格式`);
            return;
        }
        if (file.size > maxMB * 1024 * 1024) {
            setError(`文件 ${(file.size / 1024 / 1024).toFixed(1)}MB 超过 ${maxMB}MB 上限, 请压缩后再上传`);
            return;
        }
        setBusy(true);
        setError('');
        try {
            const att = await uploadAttachment(file, purpose);
            // fileUrl 是永久 CDN 地址 (2026-08-16 起前端直传 R2, 落库即最终地址).
            onChange(att.fileUrl || att.url);
        } catch (e) {
            const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            setError('上传失败: ' + msg);
        } finally {
            setBusy(false);
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) handleFile(f);
    };

    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) handleFile(f);
    };

    const hiddenInput = (
        <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={onPick} />
    );

    // 状态行统一排在方框下方, 宽度不受方框约束.
    const status = (
        <>
            {busy && <div className="text-[11px] text-slate-500 mt-1">上传中…</div>}
            {error && <div className="text-[11px] text-rose-600 mt-1 break-all">{error}</div>}
        </>
    );

    if (value) {
        return (
            <div>
                <div className="relative inline-block">
                    <img
                        src={imgSrc(value)}
                        alt="预览"
                        className={`${box} rounded border border-slate-200 object-cover bg-slate-50${onPreview ? ' cursor-zoom-in' : ''}`}
                        onClick={onPreview ? () => onPreview(imgSrc(value)) : undefined}
                        onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                    />
                    <div className="absolute -top-1 -right-1 flex gap-1">
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => inputRef.current?.click()}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                            title="替换图片"
                        >
                            ↻
                        </button>
                        <button
                            type="button"
                            disabled={busy}
                            // 二次确认: ✕ 紧贴"替换"按钮, 误触会清掉已上传的图
                            onClick={async () => {
                                if (await confirmDialog('确认清除这张图片? 保存后原图从该记录上移除.')) onChange('');
                            }}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500 text-white hover:bg-rose-600 disabled:opacity-50"
                            title="清除"
                        >
                            ×
                        </button>
                    </div>
                </div>
                {hiddenInput}
                {status}
            </div>
        );
    }

    return (
        <div>
            <div
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`${box} cursor-pointer rounded border-2 border-dashed flex flex-col items-center justify-center text-center px-2 text-[11px] leading-snug transition-colors ${
                    dragOver
                        ? 'border-brand-500 bg-brand-50 text-brand-700'
                        : busy
                          ? 'border-slate-300 bg-slate-50 text-slate-400'
                          : 'border-slate-200 text-slate-400 hover:border-brand-300 hover:text-brand-600'
                }`}
            >
                {busy ? (
                    <>上传中…</>
                ) : (
                    <>
                        <span className="text-lg leading-none">＋</span>
                        <span className="mt-1">{placeholder || '上传主图'}</span>
                    </>
                )}
            </div>
            {hiddenInput}
            {status}
        </div>
    );
}

export default ImageUploader;
